import React from 'react'
import { Box, TextField, MenuItem, Typography, InputAdornment } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface Country {
  code: string
  name: string
  flag: string
  dialCode: string
  minLength: number
  maxLength: number
}

const COUNTRIES: Country[] = [
  { code: 'ES', name: 'España', flag: '🇪🇸', dialCode: '+34', minLength: 9, maxLength: 9 },
  { code: 'GB', name: 'Reino Unido', flag: '🇬🇧', dialCode: '+44', minLength: 10, maxLength: 10 },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'SI', name: 'Eslovenia', flag: '🇸🇮', dialCode: '+386', minLength: 8, maxLength: 8 },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '+351', minLength: 9, maxLength: 9 },
  { code: 'IT', name: 'Italia', flag: '🇮🇹', dialCode: '+39', minLength: 9, maxLength: 10 },
  { code: 'FR', name: 'Francia', flag: '🇫🇷', dialCode: '+33', minLength: 9, maxLength: 9 },
  { code: 'DE', name: 'Alemania', flag: '🇩🇪', dialCode: '+49', minLength: 10, maxLength: 11 },
  { code: 'MX', name: 'México', flag: '🇲🇽', dialCode: '+52', minLength: 10, maxLength: 10 },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54', minLength: 10, maxLength: 10 },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', dialCode: '+57', minLength: 10, maxLength: 10 },
  { code: 'PE', name: 'Perú', flag: '🇵🇪', dialCode: '+51', minLength: 9, maxLength: 9 },
  { code: 'CL', name: 'Chile', flag: '🇨🇱', dialCode: '+56', minLength: 9, maxLength: 9 },
]

interface PhoneInputWithCountrySelectProps {
  value: string
  onChange: (phone: string) => void
  error?: boolean
  helperText?: string
  required?: boolean
  placeholder?: string
  fullWidth?: boolean
  disabled?: boolean
  label?: string
  name?: string
}

export function PhoneInputWithCountrySelect({
  value,
  onChange,
  error = false,
  helperText = '',
  required = false,
  fullWidth = true,
  disabled = false,
  label,
  name = 'phone',
}: PhoneInputWithCountrySelectProps) {
  const { t } = useTranslation()
  const [selectedCountry, setSelectedCountry] = React.useState<Country>(COUNTRIES[0])
  const [phoneNumber, setPhoneNumber] = React.useState('')
  const [validationError, setValidationError] = React.useState('')

  React.useEffect(() => {
    const userLang = navigator.language || 'es-ES'
    let defaultCountry = COUNTRIES[0]

    if (userLang.includes('es')) defaultCountry = COUNTRIES[0] // España
    else if (userLang.includes('en')) defaultCountry = COUNTRIES[1] // Reino Unido
    else if (userLang.includes('sl')) defaultCountry = COUNTRIES[3] // Eslovenia
    else if (userLang.includes('pt')) defaultCountry = COUNTRIES[4] // Portugal
    else if (userLang.includes('it')) defaultCountry = COUNTRIES[5] // Italia
    else if (userLang.includes('fr')) defaultCountry = COUNTRIES[6] // Francia

    setSelectedCountry(defaultCountry)

    if (value && value.trim()) {
      const matchedCountry = COUNTRIES.find((country) => value.startsWith(country.dialCode))

      if (matchedCountry) {
        setSelectedCountry(matchedCountry)
        const number = value.replace(matchedCountry.dialCode, '').trim()
        setPhoneNumber(number)
      } else {
        setPhoneNumber(value)
      }
    }
  }, [value])

  const validatePhoneNumber = (number: string, country: Country): boolean => {
    const cleanNumber = number.replace(/\s/g, '')

    if (!cleanNumber) {
      setValidationError('')
      return true
    }

    if (cleanNumber.length < country.minLength || cleanNumber.length > country.maxLength) {
      setValidationError(t('invalidPhoneFormat'))
      return false
    }

    setValidationError('')
    return true
  }

  const handleCountryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const countryCode = event.target.value
    const country = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0]
    setSelectedCountry(country)

    const fullPhone = country.dialCode + phoneNumber
    onChange(fullPhone)

    validatePhoneNumber(phoneNumber, country)
  }

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value
    const number = input.replace(/\D/g, '')
    setPhoneNumber(number)

    const fullPhone = selectedCountry.dialCode + number
    onChange(fullPhone)

    validatePhoneNumber(number, selectedCountry)
  }

  const displayError = error || !!validationError
  const displayHelperText = validationError || helperText

  return (
    <Box>
      {label && (
        <Typography mb={1} color="textPrimary">
          {label} {required && '*'}
        </Typography>
      )}
      <Box display="flex" gap={1}>
        <TextField
          select
          value={selectedCountry.code}
          onChange={handleCountryChange}
          variant="outlined"
          size="small"
          disabled={disabled}
          sx={{
            width: '120px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '32px',
              height: '40px',
            },
            '& .MuiSelect-select': {
              paddingTop: '10px',
              paddingBottom: '10px',
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {COUNTRIES.map((country) => (
            <MenuItem key={country.code} value={country.code}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontSize="1.2em">{country.flag}</Typography>
                <Typography>{country.dialCode}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          value={phoneNumber}
          onChange={handlePhoneChange}
          variant="outlined"
          size="small"
          fullWidth={fullWidth}
          required={required}
          error={displayError}
          helperText={displayHelperText}
          disabled={disabled}
          name={name}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography color="textSecondary">{selectedCountry.dialCode}</Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '32px',
              paddingLeft: '8px',
            },
            '& .MuiInputAdornment-root': {
              marginRight: '4px',
            },
          }}
        />
      </Box>
    </Box>
  )
}
